import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import { showToast } from "../../assets/util/showToast";
import { getToken } from "../../lib/tokenStorage";
import { API_BASE_URL } from "../../config";
import {
  blogPostsService,
  BlogCategory,
  BlogTag,
} from "../../services/blogPosts";

// Custom upload adapter for CKEditor
class CustomUploadAdapter {
  constructor(loader: any) {
    this.loader = loader;
  }

  private loader: any;

  upload() {
    return this.loader.file.then((file: File) => {
      return new Promise((resolve, reject) => {
        const formData = new FormData();
        formData.append("upload", file);

        const token = getToken();
        const headers: HeadersInit = {};
        if (token) {
          headers["Authorization"] = `Bearer ${token}`;
        }

        fetch(`${API_BASE_URL}/upload.php`, {
          method: "POST",
          headers,
          body: formData,
        })
          .then((response) => response.json())
          .then((data) => {
            if (data.success) {
              resolve({
                default: data.url,
              });
            } else {
              reject(data.error || "Upload failed");
            }
          })
          .catch((error) => {
            reject(error.message || "Upload failed");
          });
      });
    });
  }

  abort() {
    // Abort upload if needed
  }
}

function uploadPlugin(editor: any) {
  editor.plugins.get("FileRepository").createUploadAdapter = (loader: any) => {
    return new CustomUploadAdapter(loader);
  };
}

// Form interface for the editor
interface BlogPostForm {
  id?: number;
  title: string;
  excerpt: string;
  content: string;
  meta_title: string;
  meta_description: string;
  category_id: number;
  status: "draft" | "published" | "archived" | "scheduled";
  is_featured: boolean;
  is_comments_enabled: boolean;
  tags: number[];
  featured_image?: string;
  featured_image_alt?: string;
  scheduled_at?: string;
}

export default function CreateEditBlogPost() {
  const navigate = useNavigate();
  const { id } = useParams<{ id?: string }>();
  const isEditing = Boolean(id);

  // Form state
  const [formData, setFormData] = useState<BlogPostForm>({
    title: "",
    excerpt: "",
    content: "",
    meta_title: "",
    meta_description: "",
    category_id: 0,
    status: "draft",
    is_featured: false,
    is_comments_enabled: true,
    tags: [],
    featured_image: "",
    featured_image_alt: "",
    scheduled_at: "",
  });

  // Data state
  const [categories, setCategories] = useState<BlogCategory[]>([]);
  const [tags, setTags] = useState<BlogTag[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);

  // UI state
  const [activeTab, setActiveTab] = useState<"content" | "seo">("content");
  const [selectedTags, setSelectedTags] = useState<Set<number>>(new Set());
  const [tagSearch, setTagSearch] = useState("");

  // Load initial data
  useEffect(() => {
    loadInitialData();
  }, []);

  // Load post data callback
  const loadPost = useCallback(
    async (postId: string) => {
      try {
        setLoading(true);
        const post = await blogPostsService.getPost(postId);

        setFormData({
          id: post.id,
          title: post.title || "",
          excerpt: post.excerpt || "",
          content: post.content || "",
          meta_title: post.meta_title || "",
          meta_description: post.meta_description || "",
          category_id: post.category_id || 0,
          status: post.status || "draft",
          is_featured: Boolean(post.is_featured),
          is_comments_enabled: Boolean(post.is_comments_enabled),
          tags: post.tags?.map((tag: any) => tag.id) || [],
          featured_image: post.featured_image || "",
          featured_image_alt: post.featured_image_alt || "",
          scheduled_at: post.scheduled_at || "",
        });

        setSelectedTags(new Set(post.tags?.map((tag: any) => tag.id) || []));
      } catch (error) {
        console.error("Failed to load post:", error);
        showToast("Failed to load blog post", "error");
        navigate("/account/blog-posts");
      } finally {
        setLoading(false);
        setInitialLoading(false);
      }
    },
    [navigate]
  );

  // Load post data if editing
  useEffect(() => {
    if (isEditing && id) {
      loadPost(id);
    } else {
      setInitialLoading(false);
    }
  }, [isEditing, id, loadPost]);

  // Auto-generate meta fields when title/excerpt changes
  useEffect(() => {
    if (formData.title && !formData.meta_title) {
      setFormData((prev) => ({ ...prev, meta_title: formData.title }));
    }
    if (formData.excerpt && !formData.meta_description) {
      setFormData((prev) => ({ ...prev, meta_description: formData.excerpt }));
    }
  }, [
    formData.title,
    formData.excerpt,
    formData.meta_title,
    formData.meta_description,
  ]);

  const loadInitialData = async () => {
    try {
      const [categoriesRes, tagsRes] = await Promise.all([
        blogPostsService.getCategories(),
        blogPostsService.getTags(),
      ]);
      setCategories(categoriesRes);
      setTags(tagsRes);
    } catch (error) {
      console.error("Failed to load initial data:", error);
      showToast("Failed to load categories and tags", "error");
    }
  };

  const handleInputChange = (field: keyof BlogPostForm, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleTagToggle = (tagId: number) => {
    const newSelectedTags = new Set(selectedTags);
    if (newSelectedTags.has(tagId)) {
      newSelectedTags.delete(tagId);
    } else {
      newSelectedTags.add(tagId);
    }
    setSelectedTags(newSelectedTags);
    setFormData((prev) => ({ ...prev, tags: Array.from(newSelectedTags) }));
  };

  const handleSave = async (status?: "draft" | "published") => {
    try {
      // Validation
      if (!formData.title.trim()) {
        showToast("Title is required", "error");
        return;
      }
      if (!formData.category_id) {
        showToast("Please select a category", "error");
        return;
      }

      setSaving(true);

      const saveData = {
        ...formData,
        status: status || formData.status,
        tags: Array.from(selectedTags),
      };

      if (isEditing && id) {
        await blogPostsService.updatePost(id, saveData);
        showToast("Blog post updated successfully!", "success");
      } else {
        const response = await blogPostsService.createPost(saveData);
        showToast("Blog post created successfully!", "success");
        navigate(`/account/blog-posts/edit/${response.id}`);
      }
    } catch (error: any) {
      console.error("Failed to save post:", error);
      showToast(error.message || "Failed to save blog post", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!isEditing || !id) return;

    if (
      !window.confirm(
        "Are you sure you want to delete this blog post? This action cannot be undone."
      )
    ) {
      return;
    }

    try {
      setSaving(true);
      await blogPostsService.deletePost(id);
      showToast("Blog post deleted successfully!", "success");
      navigate("/account/blog-posts");
    } catch (error: any) {
      console.error("Failed to delete post:", error);
      showToast(error.message || "Failed to delete blog post", "error");
    } finally {
      setSaving(false);
    }
  };

  if (initialLoading) {
    return (
      <div className="page-layout">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-layout">
      <div className="page-header">
        <h2 className="title">
          {isEditing ? "Edit Blog Post" : "Create New Blog Post"}
        </h2>
        <div className="right">
          <button
            className="btn btn-secondary"
            onClick={() => navigate("/account/blog-posts")}
            disabled={saving}
          >
            <i className="fa-solid fa-arrow-left"></i> Back to Posts
          </button>

          <button
            className="btn btn-primary"
            onClick={() => handleSave("draft")}
            disabled={saving || !formData.title.trim()}
          >
            {saving ? (
              <>
                <i className="fa-solid fa-spinner fa-spin"></i> Saving...
              </>
            ) : (
              <>
                <i className="fa-solid fa-save"></i> Save Draft
              </>
            )}
          </button>

          <button
            className="btn btn-primary"
            onClick={() => handleSave("published")}
            disabled={saving || !formData.title.trim() || !formData.category_id}
          >
            {saving ? (
              <>
                <i className="fa-solid fa-spinner fa-spin"></i> Publishing...
              </>
            ) : (
              <>
                <i className="fa-solid fa-globe"></i>{" "}
                {isEditing ? "Update & Publish" : "Publish"}
              </>
            )}
          </button>

          {isEditing && (
            <button
              className="btn"
              style={{ color: "#dc3545", borderColor: "#dc3545" }}
              onClick={handleDelete}
              disabled={saving}
            >
              <i className="fa-solid fa-trash"></i> Delete
            </button>
          )}
        </div>
      </div>

      <div className="blog-editor mt-4">
        {/* Tab Navigation */}
        <div className="editor-tabs">
          <button
            className={`tab ${activeTab === "content" ? "active" : ""}`}
            onClick={() => setActiveTab("content")}
          >
            <i className="fa-solid fa-edit"></i> Content
          </button>
          <button
            className={`tab ${activeTab === "seo" ? "active" : ""}`}
            onClick={() => setActiveTab("seo")}
          >
            <i className="fa-solid fa-search"></i> SEO
          </button>
        </div>

        {/* Tab Content */}
        <div className="tab-content">
          {activeTab === "content" && (
            <div className="editor-content">
              <div className="editor-main">
                <div className="form-group">
                  <label htmlFor="title">Title *</label>
                  <input
                    id="title"
                    type="text"
                    value={formData.title}
                    onChange={(e) => handleInputChange("title", e.target.value)}
                    placeholder="Enter blog post title..."
                    className="form-control"
                    maxLength={255}
                  />
                  <small className="form-text">
                    {formData.title.length}/255 characters
                  </small>
                </div>

                <div className="form-group">
                  <label htmlFor="featured_image">Featured Image</label>
                  <div className="image-upload-group">
                    <div className="image-upload-input">
                      <input
                        id="featured_image"
                        type="text"
                        value={formData.featured_image}
                        onChange={(e) =>
                          handleInputChange("featured_image", e.target.value)
                        }
                        placeholder="Image URL or upload below..."
                        className="form-control"
                      />
                    </div>
                    <div className="image-upload-button">
                      <label
                        htmlFor="featured_image_input"
                        className="btn btn-secondary"
                      >
                        <i className="fa-solid fa-upload"></i> Upload Image
                      </label>
                      <input
                        id="featured_image_input"
                        type="file"
                        accept="image/*"
                        style={{ display: "none" }}
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            try {
                              const formDataUpload = new FormData();
                              formDataUpload.append("upload", file);
                              const token = getToken();
                              const headers: HeadersInit = {};
                              if (token) {
                                headers["Authorization"] = `Bearer ${token}`;
                              }
                              const response = await fetch(
                                `${API_BASE_URL}/upload.php`,
                                {
                                  method: "POST",
                                  headers,
                                  body: formDataUpload,
                                }
                              );
                              const data = await response.json();
                              if (data.success) {
                                handleInputChange("featured_image", data.url);
                                handleInputChange(
                                  "featured_image_alt",
                                  file.name
                                );
                                showToast(
                                  "Image uploaded successfully!",
                                  "success"
                                );
                              } else {
                                showToast(
                                  data.error || "Upload failed",
                                  "error"
                                );
                              }
                            } catch (error: any) {
                              showToast(
                                error.message || "Upload failed",
                                "error"
                              );
                            }
                          }
                        }}
                      />
                    </div>
                  </div>
                  {formData.featured_image && (
                    <div className="image-preview">
                      <img
                        src={formData.featured_image}
                        alt={formData.featured_image_alt}
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = "none";
                        }}
                        style={{
                          maxWidth: "100%",
                          height: "auto",
                          borderRadius: "8px",
                          marginTop: "10px",
                        }}
                      />
                    </div>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="featured_image_alt">
                    Featured Image Alt Text
                  </label>
                  <input
                    id="featured_image_alt"
                    type="text"
                    value={formData.featured_image_alt}
                    onChange={(e) =>
                      handleInputChange("featured_image_alt", e.target.value)
                    }
                    placeholder="Descriptive alt text for the image..."
                    className="form-control"
                  />
                  <small className="form-text">
                    Used for accessibility and SEO
                  </small>
                </div>

                <div className="form-group">
                  <label htmlFor="excerpt">Excerpt</label>
                  <textarea
                    id="excerpt"
                    value={formData.excerpt}
                    onChange={(e) =>
                      handleInputChange("excerpt", e.target.value)
                    }
                    placeholder="Brief description of your post..."
                    className="form-control"
                    rows={3}
                    maxLength={500}
                  />
                  <small className="form-text">
                    {formData.excerpt.length}/500 characters
                  </small>
                </div>

                <div className="form-group">
                  <label htmlFor="content">Content</label>
                  <CKEditor
                    editor={ClassicEditor}
                    data={formData.content}
                    onChange={(event, editor) => {
                      const data = editor.getData();
                      handleInputChange("content", data);
                    }}
                    onReady={(editor) => {
                      editor.plugins.get("FileRepository").createUploadAdapter =
                        (loader: any) => {
                          return new CustomUploadAdapter(loader);
                        };
                    }}
                    config={{
                      toolbar: {
                        items: [
                          "heading",
                          "|",
                          "bold",
                          "italic",
                          "link",
                          "bulletedList",
                          "numberedList",
                          "blockQuote",
                          "codeBlock",
                          "insertTable",
                          "tableColumn",
                          "tableRow",
                          "mergeTableCells",
                          "imageUpload",
                          "mediaEmbed",
                          "undo",
                          "redo",
                        ],
                      },
                      image: {
                        toolbar: [
                          "imageTextAlternative",
                          "imageStyle:inline",
                          "imageStyle:block",
                          "imageStyle:side",
                          "linkImage",
                        ],
                      },
                      language: "en",
                      table: {
                        contentToolbar: [
                          "tableColumn",
                          "tableRow",
                          "mergeTableCells",
                        ],
                      },
                    }}
                  />
                  <small className="form-text">
                    {formData.content.replace(/<[^>]*>/g, "").length} characters
                    • ~
                    {Math.ceil(
                      formData.content.replace(/<[^>]*>/g, "").split(" ")
                        .length / 200
                    )}{" "}
                    min read
                  </small>
                </div>
              </div>

              <div className="editor-sidebar">
                <div className="sidebar-section">
                  <h4>Publish Status</h4>
                  <select
                    value={formData.status}
                    onChange={(e) =>
                      handleInputChange("status", e.target.value)
                    }
                    className="form-control"
                  >
                    <option value="draft">Draft</option>
                    <option value="published">Published</option>
                    <option value="archived">Archived</option>
                    <option value="scheduled">Scheduled</option>
                  </select>

                  {formData.status === "scheduled" && (
                    <div className="mt-2">
                      <label htmlFor="scheduled_at">Schedule Date</label>
                      <input
                        id="scheduled_at"
                        type="datetime-local"
                        value={formData.scheduled_at}
                        onChange={(e) =>
                          handleInputChange("scheduled_at", e.target.value)
                        }
                        className="form-control"
                        min={new Date().toISOString().slice(0, 16)}
                      />
                    </div>
                  )}
                </div>

                <div className="sidebar-section">
                  <h4>Category *</h4>
                  <select
                    value={formData.category_id}
                    onChange={(e) =>
                      handleInputChange("category_id", parseInt(e.target.value))
                    }
                    className="form-control"
                  >
                    <option value={0}>Select a category...</option>
                    {categories.map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* <div className="sidebar-section">
                  <h4>Tags</h4>
                  <input
                    type="text"
                    placeholder="Search tags..."
                    value={tagSearch}
                    onChange={(e) => setTagSearch(e.target.value)}
                    className="form-control mb-2"
                  />
                  <div className="tags-list">
                    {filteredTags.map((tag) => (
                      <label key={tag.id} className="tag-checkbox">
                        <input
                          type="checkbox"
                          checked={selectedTags.has(tag.id)}
                          onChange={() => handleTagToggle(tag.id)}
                        />
                        <span className="tag-name" style={{ color: tag.color }}>
                          {tag.name}
                        </span>
                      </label>
                    ))}
                  </div>
                  {selectedTags.size > 0 && (
                    <div className="selected-tags">
                      <small>
                        Selected: {Array.from(selectedTags).length} tags
                      </small>
                    </div>
                  )}
                </div> */}

                <div className="sidebar-section">
                  <h4>Options</h4>
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={formData.is_featured}
                      onChange={(e) =>
                        handleInputChange("is_featured", e.target.checked)
                      }
                    />
                    <span>Featured Post</span>
                  </label>
                </div>
              </div>
            </div>
          )}

          {activeTab === "seo" && (
            <div className="seo-content">
              <div className="form-group">
                <label htmlFor="meta_title">SEO Title</label>
                <input
                  id="meta_title"
                  type="text"
                  value={formData.meta_title}
                  onChange={(e) =>
                    handleInputChange("meta_title", e.target.value)
                  }
                  placeholder="SEO optimized title..."
                  className="form-control"
                  maxLength={60}
                />
                <small className="form-text">
                  {formData.meta_title.length}/60 characters (recommended)
                </small>
              </div>

              <div className="form-group">
                <label htmlFor="meta_description">Meta Description</label>
                <textarea
                  id="meta_description"
                  value={formData.meta_description}
                  onChange={(e) =>
                    handleInputChange("meta_description", e.target.value)
                  }
                  placeholder="Brief description for search engines..."
                  className="form-control"
                  rows={3}
                  maxLength={160}
                />
                <small className="form-text">
                  {formData.meta_description.length}/160 characters
                  (recommended)
                </small>
              </div>

              <div className="seo-preview">
                <h4>Search Engine Preview</h4>
                <div className="preview-card">
                  <div className="preview-title">
                    {formData.meta_title ||
                      formData.title ||
                      "Your Blog Post Title"}
                  </div>
                  <div className="preview-url">
                    example.com/blog/
                    {formData.title.toLowerCase().replace(/\s+/g, "-")}
                  </div>
                  <div className="preview-description">
                    {formData.meta_description ||
                      formData.excerpt ||
                      "Your blog post description will appear here..."}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
